import type { AnswerPhase, VisualizationType } from "@unbox/api";

export type StreamingPhase = { phase: AnswerPhase; displayType: VisualizationType | null };

export type DisplayKind = "number" | "list" | "videos" | "table" | "chart";

export function displayKind(type: VisualizationType): DisplayKind {
  switch (type) {
    case "metric_strip":
    case "metric_grid":
      return "number";
    case "list":
    case "videos":
    case "table":
      return type;
    default:
      return "chart";
  }
}

const DISPLAY_MESSAGE: Record<DisplayKind, string> = {
  number: "Pulling out the headline numbers…",
  list: "Building the list…",
  videos: "Finding the videos that show this…",
  table: "Laying out the table…",
  chart: "Drawing the chart…",
};

export type StreamState = {
  stepRunning: boolean;
  hasSteps: boolean;
  hasText: boolean;
  phase?: StreamingPhase;
  displayArrived: boolean;
  stopped?: boolean;
};

/**
 * The status line under a streaming answer: what is being prepared right now.
 * Null while a query runs, since its Sources pill already shows a spinner and
 * label, and before the first query, where the thinking dots show instead.
 */
export function streamStatus(s: StreamState): string | null {
  if (s.stopped || s.stepRunning) return null;
  if (!s.phase) {
    if (s.hasText) return "Writing the answer…";
    return s.hasSteps ? "Reading the results…" : null;
  }
  switch (s.phase.phase) {
    case "choosing":
      return "Choosing how to show this…";
    case "display":
      if (s.displayArrived) return "Finishing the answer…";
      return s.phase.displayType ? DISPLAY_MESSAGE[displayKind(s.phase.displayType)] : "Choosing how to show this…";
    case "findings":
      return "Noting the key findings…";
    case "followups":
      return "Suggesting what to explore next…";
    case "details":
      return "Wrapping up…";
  }
}

/** The display type to hold space for with a skeleton, while it is known but not yet arrived. */
export function pendingDisplay(phase: StreamingPhase | undefined, displayArrived: boolean): DisplayKind | null {
  if (displayArrived || !phase?.displayType) return null;
  return displayKind(phase.displayType);
}

const STARTING_PHRASES = ["Reading your question…", "Finding the right data…", "Planning the analysis…"];

const QUERY_PHRASES: { match: RegExp; phrase: string }[] = [
  { match: /safety|flag|risk/i, phrase: "Checking safety…" },
  { match: /trend|week|wow|daily|monthly|over time/i, phrase: "Tracing the trend over time…" },
  { match: /creator/i, phrase: "Ranking your creators…" },
  { match: /sample/i, phrase: "Checking samples…" },
  { match: /sequence|outreach|repl/i, phrase: "Reading outreach replies…" },
  { match: /product/i, phrase: "Looking at your products…" },
  { match: /video/i, phrase: "Scanning your videos…" },
  { match: /gmv|revenue|sales|headline/i, phrase: "Pulling GMV and activity…" },
];

export type PillState = StreamState & {
  /** The running query's label, else the last finished one's. */
  stepLabel?: string;
  /** Counts up while nothing has streamed yet, to step through the opening phrases. */
  tick: number;
  /** Report templates write their own progress labels, which read better than a topic phrase. */
  labelAsWritten?: boolean;
};

/**
 * The drawer's status pill. While data is fetched it names the topic of the query;
 * once the model writes the answer it names the part being prepared, including the
 * display type, using the same messages as the page's status line.
 */
export function pillStatus(s: PillState): string | null {
  if (s.stopped) return null;
  if (s.phase) return streamStatus({ ...s, stepRunning: false });
  if (s.hasText) return "Writing up what I found…";
  if (s.stepLabel) {
    if (s.labelAsWritten) return `${s.stepLabel}…`;
    return QUERY_PHRASES.find((q) => q.match.test(s.stepLabel ?? ""))?.phrase ?? `${s.stepLabel}…`;
  }
  return STARTING_PHRASES[Math.min(s.tick, STARTING_PHRASES.length - 1)];
}
